import { HP_DEFAULT_MAX, MOMENTUM_MAX } from '@trames/shared/constants';

/**
 * HUD de combate (TKT-029): barra HP del player + barra Ímpetu + HP de enemigos.
 *
 * Colores paleta visual-direction.md:
 *   - HP: sangre `#FF3D7F`.
 *   - Ímpetu: ámbar `#FFB347`.
 *   - Hueso (texto base): `#F4ECD8`.
 *
 * El HUD vive en HTML/CSS sobre el canvas Phaser — más rápido de iterar que
 * Phaser GameObjects para texto/barras.
 */

const SANGRE = '#FF3D7F';
const AMBAR = '#FFB347';
const HUESO = '#F4ECD8';

export interface HudCombatState {
  playerHp: number;
  playerHpMax: number;
  playerMomentum: number;
  enemies: Map<string, { hp: number; hpMax: number; alias: string }>;
}

export class HudCombat {
  private root: HTMLElement;
  private hpFill!: HTMLElement;
  private hpLabel!: HTMLElement;
  private momentumFill!: HTMLElement;
  private momentumLabel!: HTMLElement;
  private enemiesEl!: HTMLElement;
  private accessibilityReduceMotion = false;

  constructor(parent: HTMLElement = document.body) {
    this.root = document.createElement('div');
    this.root.dataset['testid'] = 'hud-combat';
    this.root.style.cssText = `
      position: fixed;
      left: 16px;
      bottom: 16px;
      color: ${HUESO};
      font-family: ui-monospace, monospace;
      pointer-events: none;
      z-index: 50;
    `;
    this.root.innerHTML = `
      <div data-testid="hud-hp" style="margin-bottom:8px">
        <div style="font-size:12px;opacity:0.7">HP</div>
        <div style="width:240px;height:14px;background:#1c1a23;border:1px solid ${HUESO}33;border-radius:6px;overflow:hidden">
          <div data-testid="hud-hp-fill" style="height:100%;width:100%;background:${SANGRE};transition:width 80ms linear"></div>
        </div>
        <div data-testid="hud-hp-label" style="font-size:14px;margin-top:2px"></div>
      </div>
      <div data-testid="hud-momentum">
        <div style="font-size:12px;opacity:0.7">Ímpetu</div>
        <div style="width:240px;height:10px;background:#1c1a23;border:1px solid ${HUESO}33;border-radius:6px;overflow:hidden">
          <div data-testid="hud-momentum-fill" style="height:100%;width:0%;background:${AMBAR};transition:width 80ms linear"></div>
        </div>
        <div data-testid="hud-momentum-label" style="font-size:14px;margin-top:2px"></div>
      </div>
      <div data-testid="hud-enemies" style="margin-top:12px"></div>
    `;
    parent.appendChild(this.root);

    this.hpFill = this.root.querySelector<HTMLElement>('[data-testid="hud-hp-fill"]')!;
    this.hpLabel = this.root.querySelector<HTMLElement>('[data-testid="hud-hp-label"]')!;
    this.momentumFill = this.root.querySelector<HTMLElement>('[data-testid="hud-momentum-fill"]')!;
    this.momentumLabel = this.root.querySelector<HTMLElement>(
      '[data-testid="hud-momentum-label"]',
    )!;
    this.enemiesEl = this.root.querySelector<HTMLElement>('[data-testid="hud-enemies"]')!;

    // Respeta prefers-reduced-motion para el screen flash (TKT-029 AC).
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.accessibilityReduceMotion = mq.matches;
    }
  }

  setReduceMotion(reduce: boolean): void {
    this.accessibilityReduceMotion = reduce;
  }

  getReduceMotion(): boolean {
    return this.accessibilityReduceMotion;
  }

  update(state: HudCombatState): void {
    const hpPct = Math.max(0, Math.min(1, state.playerHp / (state.playerHpMax || HP_DEFAULT_MAX)));
    this.hpFill.style.width = `${hpPct * 100}%`;
    this.hpLabel.textContent = `${state.playerHp}/${state.playerHpMax}`;

    const momPct = Math.max(0, Math.min(1, state.playerMomentum / MOMENTUM_MAX));
    this.momentumFill.style.width = `${momPct * 100}%`;
    this.momentumLabel.textContent = `${state.playerMomentum}/${MOMENTUM_MAX}`;

    // Render enemies (en spike 6 solo el Dummy).
    this.enemiesEl.innerHTML = '';
    for (const [id, e] of state.enemies) {
      const row = document.createElement('div');
      row.dataset['enemyId'] = id;
      row.dataset['testid'] = 'hud-enemy';
      const ratio = Math.max(0, Math.min(1, e.hp / (e.hpMax || 1)));
      row.style.cssText = 'margin-top:6px;font-size:13px';
      row.innerHTML = `
        <div>${escapeHtml(e.alias)}</div>
        <div style="width:160px;height:8px;background:#1c1a23;border:1px solid ${HUESO}33;border-radius:4px;overflow:hidden">
          <div style="height:100%;width:${ratio * 100}%;background:${SANGRE};transition:width 80ms linear"></div>
        </div>
      `;
      this.enemiesEl.appendChild(row);
    }
  }

  destroy(): void {
    this.root.remove();
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export { SANGRE, AMBAR, HUESO };
